import { useEffect, useState } from "react";
import { toAbsoluteUrl } from "@/components/ImageUpload";
import { useContent } from "@/context/ContentContext";
import { api, isAllowedImage } from "@/lib/api";
import { toast } from "sonner";
import { Upload } from "lucide-react";

export default function CustomRugs() {
  const pc = useContent().pages.custom_rugs;
  const [photos, setPhotos] = useState([]);
  useEffect(() => { api.get("/gallery", { params: { category: "custom_rugs" } }).then((r) => setPhotos(r.data)).catch(() => {}); }, []);
  const hero = photos.find((p) => p.featured) || photos[0];
  const rest = photos.filter((p) => p !== hero);
  const [form, setForm] = useState({ name: "", email: "", phone: "", width: 24, height: 24, shape: "rectangle", colors: "", use: "", needed_by: "", pickup_delivery: "pickup", notes: "" });
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!isAllowedImage(f, true)) return toast.error("Please upload a HEIC, JPG, PNG or PDF file");
    if (f.size > 4_000_000) return toast.error("File too large (max 4MB)");
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    let artwork_data = null, artwork_name = null;
    if (file) {
      artwork_data = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
      artwork_name = file.name;
    }
    try {
      const { data } = await api.post("/custom-rugs", { ...form, width: Number(form.width), height: Number(form.height), artwork_data, artwork_name });
      setResult(data);
      toast.success("Inquiry sent!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error("Could not submit"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
      <div className="max-w-2xl">
        <div className="text-terracotta text-sm tracking-widest uppercase font-medium">{pc.eyebrow}</div>
        <h1 className="mt-2 font-serif font-bold text-5xl md:text-6xl leading-tight">{pc.heading}</h1>
        <p className="mt-4 text-lg text-charcoal/70">{pc.description}</p>
      </div>

      {hero && (
        <div className="mt-10 grid md:grid-cols-[3fr_2fr] gap-4" data-testid="custom-rug-showcase">
          <img src={toAbsoluteUrl(hero.url)} alt={hero.caption || "Custom rug"} className="rounded-[2rem] w-full h-72 md:h-[420px] object-cover shadow-hover border-4 border-white" data-testid="custom-rug-hero"/>
          {rest.length > 0 && (
            <div className="grid grid-cols-2 gap-3 content-start">
              {rest.slice(0, 4).map((p) => <img key={p.id} src={toAbsoluteUrl(p.url)} alt={p.caption || "Custom rug"} className="rounded-2xl w-full h-32 md:h-[200px] object-cover shadow-card" data-testid={`custom-rug-photo-${p.id}`}/>)}
            </div>
          )}
        </div>
      )}
      {rest.length > 4 && (
        <div className="mt-4 flex gap-3 overflow-x-auto pb-2" data-testid="custom-rug-strip">
          {rest.slice(4).map((p) => <img key={p.id} src={toAbsoluteUrl(p.url)} alt={p.caption || "Custom rug"} className="rounded-2xl h-28 w-40 shrink-0 object-cover shadow-card"/>)}
        </div>
      )}

      {result && (
        <div className="mt-10 bg-forest text-white rounded-3xl p-8 shadow-card text-center" data-testid="custom-rug-estimate">
          <h3 className="font-serif text-2xl">Preliminary estimate</h3>
          <div className="mt-2 font-serif text-4xl font-bold">${Math.round(result.estimate).toLocaleString()}</div>
          <p className="mt-2 text-white/80 text-sm">Not a final quote. Our team will follow up with a detailed proposal.</p>
        </div>
      )}

      <form onSubmit={submit} className="mt-10 bg-white rounded-3xl p-8 shadow-card grid md:grid-cols-2 gap-4" data-testid="custom-rug-form">
        <F label="Name *" v={form.name} on={(v) => setForm({...form, name: v})} req name="name"/>
        <F label="Email *" v={form.email} on={(v) => setForm({...form, email: v})} type="email" req name="email"/>
        <F label="Phone" v={form.phone} on={(v) => setForm({...form, phone: v})} name="phone"/>
        <Sel label="Shape" v={form.shape} on={(v) => setForm({...form, shape: v})} opts={["rectangle", "square", "circle", "oval", "custom"]} name="shape"/>
        <F label="Desired Width (inches)" v={form.width} on={(v) => setForm({...form, width: v})} type="number" name="width"/>
        <F label="Desired Height (inches)" v={form.height} on={(v) => setForm({...form, height: v})} type="number" name="height"/>
        <F label="Colors" v={form.colors} on={(v) => setForm({...form, colors: v})} placeholder="e.g. cream, forest, terracotta" name="colors"/>
        <F label="Intended Use" v={form.use} on={(v) => setForm({...form, use: v})} placeholder="Bedroom, gift, wall art…" name="use"/>
        <F label="Needed By" v={form.needed_by} on={(v) => setForm({...form, needed_by: v})} type="date" name="needed_by"/>
        <Sel label="Pickup or Delivery" v={form.pickup_delivery} on={(v) => setForm({...form, pickup_delivery: v})} opts={["pickup", "delivery"]} name="pickup_delivery"/>
        <div className="md:col-span-2">
          <label className="text-sm font-medium">Notes</label>
          <textarea data-testid="cr-notes" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium">Upload Artwork (HEIC, JPG, PNG, PDF · max 4MB)</label>
          <label className="mt-1 flex items-center gap-3 rounded-2xl border-2 border-dashed border-cream-alt px-4 py-8 cursor-pointer hover:border-terracotta" data-testid="cr-file-label">
            <Upload size={20} className="text-terracotta"/>
            <span className="text-sm text-charcoal/70">{file ? file.name : "Click to select a file"}</span>
            <input data-testid="cr-file" type="file" accept="image/jpeg,image/png,image/heic,image/heif,application/pdf,.heic,.heif,.jpg,.jpeg,.png,.pdf" className="hidden" onChange={onFile}/>
          </label>
        </div>
        <div className="md:col-span-2 mt-4">
          <button data-testid="cr-submit" type="submit" className="w-full rounded-full bg-terracotta text-white px-8 py-4 font-semibold shadow-button">Send Inquiry</button>
        </div>
      </form>
    </div>
  );
}
function F({ label, v, on, type = "text", req, name, ...rest }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input data-testid={`cr-${name}`} required={req} type={type} value={v} onChange={(e) => on(e.target.value)} {...rest}
        className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
    </div>
  );
}
function Sel({ label, v, on, opts, name }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <select data-testid={`cr-${name}`} value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 bg-white focus:ring-2 focus:ring-terracotta focus:outline-none">
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
